import React, { useState, useEffect } from "react";
import {
    Drawer,
    Box,
    Typography,
    IconButton,
    Divider,
    Stack,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    TextField,
    InputAdornment,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { fetchCompetitionFilterList } from "../../services/Competition";

const OpenFilterDrawer = ({ open, onClose, onApplyFilters }) => {

    const [filters, setFilters] = useState({});
    const [selectedFilters, setSelectedFilters] = useState({
        search: "",
    });

    // Fetch filter list when drawer opens
    useEffect(() => {
        const fetchFilters = async () => {
            try {
                const res = await fetchCompetitionFilterList();
                const data = res.data || {};
                setFilters(data);

                // 🟢 Set default "All" selections
                const defaultValues = {
                    search: "",
                };

                if (data.sort_by?.length) defaultValues.sort_by = data.sort_by[0].value;
                if (data.statuses?.length) defaultValues.status = data.statuses[0].value;
                if (data.modules?.length) defaultValues.module = data.modules[0].value;

                if (data.competition_dates) {
                    Object.keys(data.competition_dates).forEach((key) => {
                        const list = data.competition_dates[key];
                        if (list?.length) defaultValues[key] = list[0].value;
                    });
                }

                setSelectedFilters(defaultValues);
            } catch (error) {
                console.error("Error fetching filter options:", error);
            }
        };
        fetchFilters();
    }, []);

    // Handle dropdown change
    const handleChange = (category, value) => {
        setSelectedFilters((prev) => ({
            ...prev,
            [category]: value,
        }));
    };

    // Apply filters and generate query string
    const handleApply = () => {
        const queryString = Object.entries(selectedFilters)
            .filter(([_, v]) => v && v !== "")
            .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
            .join("&");

        console.log("Filter Query →", queryString);

        if (onApplyFilters) {
            onApplyFilters(selectedFilters); // ✅ send filters to parent
        }
    };


    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{
                zIndex: 1201,
                "& .MuiDrawer-paper": {
                    width: "100%",
                    maxWidth: 320,
                    borderRadius: 0,
                },
            }}
            transitionDuration={300}
        >
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" p={2}>
                <Typography variant="h6">Filter Competitions</Typography>
                <IconButton onClick={onClose}>
                    <Close />
                </IconButton>
            </Stack>

            <Divider />

            {/* Content */}
            <Box sx={{ p: 2, overflowY: "auto", flexGrow: 1 }}>
                {/* Search input */}
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Search"
                    placeholder="Search competitions..."
                    margin="dense"
                    value={selectedFilters.search}
                    onChange={(e) => handleChange("search", e.target.value)}
                    slotProps={{
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        onClick={() => handleChange("search", "")}
                                    >
                                        <Close />
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                {/* Sort By */}
                {filters.sort_by && (
                    <FormControl fullWidth size="small" margin="normal">
                        <InputLabel>Sort By</InputLabel>
                        <Select
                            value={selectedFilters.sort_by || ""}
                            label="Sort By"
                            onChange={(e) => handleChange("sort_by", e.target.value)}
                        >
                            {filters.sort_by.map((item) => (
                                <MenuItem key={item.id} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Status */}
                {filters.statuses && (
                    <FormControl fullWidth size="small" margin="normal">
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={selectedFilters.status || ""}
                            label="Status"
                            onChange={(e) => handleChange("status", e.target.value)}
                        >
                            {filters.statuses.map((item) => (
                                <MenuItem key={item.id} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Module */}
                {filters.modules && (
                    <FormControl fullWidth size="small" margin="normal">
                        <InputLabel>Module</InputLabel>
                        <Select
                            value={selectedFilters.module || ""}
                            label="Module"
                            onChange={(e) => handleChange("module", e.target.value)}
                        >
                            {filters.modules.map((item) => (
                                <MenuItem key={item.id} value={item.value}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Competition Dates */}
                {filters.competition_dates && (
                    <Box mt={2}>
                        <Typography variant="body1" gutterBottom>
                            Competition Dates
                        </Typography>

                        <Grid container spacing={1}>
                            {Object.entries(filters.competition_dates).map(([key, list]) => (
                                <Grid size={12} key={key}>
                                    <FormControl fullWidth size="small" margin="dense">
                                        <InputLabel sx={{ textTransform: "capitalize" }}>
                                            {key.replaceAll("_", " ")}
                                        </InputLabel>
                                        <Select
                                            value={selectedFilters[key] || ""}
                                            label={key.replaceAll("_", " ")}
                                            onChange={(e) => handleChange(key, e.target.value)}
                                        >
                                            {list.map((item) => (
                                                <MenuItem key={item.id} value={item.value}>
                                                    {item.label}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}
            </Box>

            {/* Footer */}
            <Stack direction="row" alignItems="center" p={2} >
                <Button
                    variant="outlined"
                    sx={{ textTransform: "none", mr: 1 }}
                    onClick={() => setSelectedFilters({ search: "" })}
                >
                    Clear
                </Button>
                <Button
                    fullWidth
                    variant="contained"
                    disableElevation
                    sx={{ textTransform: "none" }}
                    onClick={handleApply}
                >
                    Apply Filters
                </Button>
            </Stack>
        </Drawer>
    );
};

export default OpenFilterDrawer;
